import React, { FC, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { userGrants, userInfiniteFreeTier } from '../redux/slices/user';
import Loading from '../components/layout/Loading';
import { RequestStatus } from '../redux/utils';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { intersection } from 'lodash';
import { readOrganisation } from '../redux/slices/organisation';
import { useOrganisationGrants } from '../hooks/useOrganisationGrants';
import { setPreferredLanguage } from '../utils/language';
import { needsFreeTierEnrolment } from '../components/ui/organisms/Homepage/upgradePrompt';

export const withProtected = (C: FC, roles: string[] = [], skipProfileCheck = false) => {
	const ProtectedRoute = (props: any) => {
		const navigate = useNavigate();
		const dispatch = useAppDispatch();
		const checkedExtendCreditOrgId = React.useRef<string | null>(null);
		const hasRenderedContent = useRef(false);
		const hasMeasuredContent = useRef(false);
		const enrolmentRequested = useRef(false);
		const grantsRefetched = useRef(false);

		const { user, readUserRequest, authenticated, unauthenticated, groups } = useAuthenticatedUser();
		const [currentOrganisation, userGrantsRequest, userGrant, freeTierRequest] = useAppSelector((state) => [
			state.organisation.current,
			state.user.usersGrantsRequest,
			state.user.userGrantsList,
			state.user.userInfiniteFreeTierRequest
		]);
		const { organisationGrants } = useOrganisationGrants(currentOrganisation?.id ?? '');

		// NOTE: Authentication + core data load + MFA enforcement live in ProtectedLayout, the
		// shared parent route. By the time this page renders, the user is authenticated, the
		// user record has resolved, and MFA is satisfied. This HOC only handles per-page
		// concerns: role authorization, profile/onboarding redirects and content gating.

		useEffect(() => {
			if (!authenticated) return; // handled later
			if (readUserRequest.status === RequestStatus.ERROR) return navigate('/error');
			if (!roles.length || intersection(roles, groups ?? [])) return;
			navigate('/404');
		}, [groups, authenticated, roles, readUserRequest.status]);

		// A logged-in user's saved language preference wins across devices.
		useEffect(() => {
			if (user?.language) {
				setPreferredLanguage(user.language);
			}
		}, [user?.language]);

		useEffect(() => {
			if (unauthenticated) {
				navigate('/login');
			} else if (authenticated && !user && readUserRequest.status !== RequestStatus.PENDING) {
				// Organisation requires auth confirmation — safe to dispatch here
				currentOrganisation?.id && dispatch(readOrganisation(currentOrganisation?.id));
			} else if (authenticated && user && userGrantsRequest.status !== RequestStatus.PENDING) {
				currentOrganisation?.id && dispatch(readOrganisation(currentOrganisation?.id));
			}
		}, [authenticated, unauthenticated]);

		useEffect(() => {
			if (!authenticated || !currentOrganisation || organisationGrants === undefined) return;
			if (checkedExtendCreditOrgId.current === currentOrganisation.id) return;
			checkedExtendCreditOrgId.current = currentOrganisation.id;
			if (!currentOrganisation.extendCredit && organisationGrants?.firstTimeSetup) {
				if (window.location.pathname.startsWith('/organisations')) {
					navigate(`/organisations/${currentOrganisation.id}`);
				}
			}
		}, [currentOrganisation?.id, organisationGrants, authenticated]);

		// Ensure grants are loaded when the onboarding decision is needed
		useEffect(() => {
			if (
				authenticated &&
				readUserRequest.status === RequestStatus.SUCCESS &&
				user?.confirmed &&
				!skipProfileCheck &&
				userGrantsRequest.status === RequestStatus.IDLE
			) {
				dispatch(userGrants());
			}
		}, [authenticated, readUserRequest.status, user?.confirmed, skipProfileCheck, userGrantsRequest.status]);

		useEffect(() => {
			if (
				authenticated &&
				readUserRequest.status === RequestStatus.SUCCESS &&
				!user?.confirmed &&
				!skipProfileCheck
			) {
				navigate('/profile/edit', { state: { signUp: true } });
			}
			// There is no longer an onboarding route to redirect to. Plan selection was the
			// last step of sign-up; the paid plans are now offered by UpgradeLicenseDialog
			// on the homepage instead, and nothing is gated behind choosing one.
		}, [authenticated, readUserRequest, user?.confirmed, skipProfileCheck]);

		// Free-tier backstop. Enrolment is the PostConfirmation trigger's job; this only
		// covers what a trigger cannot — a transient failure during sign-up, and accounts
		// created before automatic enrolment existed. Deliberately silent: nothing is
		// gated on it, so the page renders normally and the upgrade prompt appears once
		// the refreshed grants arrive rather than behind a loading screen.
		useEffect(() => {
			if (enrolmentRequested.current) return;
			if (userGrantsRequest.status !== RequestStatus.SUCCESS) return;
			if (!needsFreeTierEnrolment(userGrant)) return;

			// Ref rather than request status: numeric allowances accumulate on the grants
			// record, so a second dispatch before the first resolves would show up to the
			// user as extra wallet storage.
			enrolmentRequested.current = true;
			dispatch(userInfiniteFreeTier());
		}, [dispatch, userGrantsRequest.status, userGrant]);

		// Re-read grants so the new licence reaches the upgrade prompt and the sidebar
		// without a page reload. Guarded by a ref because the request status stays
		// SUCCESS in the store: every later route change mounts a fresh instance of this
		// HOC, which would otherwise re-fetch grants on every navigation.
		useEffect(() => {
			if (grantsRefetched.current) return;
			if (!enrolmentRequested.current) return;
			if (freeTierRequest.status !== RequestStatus.SUCCESS) return;

			grantsRefetched.current = true;
			dispatch(userGrants());
		}, [dispatch, freeTierRequest.status]);

		// Measure time from early load start to first content render (once)
		useEffect(() => {
			if (hasMeasuredContent.current || !hasRenderedContent.current) return;
			hasMeasuredContent.current = true;
			try {
				performance.mark('withProtected:contentRendered');
				performance.measure(
					'withProtected:totalToContent',
					'withProtected:earlyLoad:start',
					'withProtected:contentRendered'
				);
			} catch {
				// Start mark missing — earlyLoad didn't fire for this instance
			} finally {
				performance.clearMarks('withProtected:contentRendered');
				performance.clearMarks('withProtected:earlyLoad:start');
			}
		});

		if (
			!hasRenderedContent.current &&
			(readUserRequest.status === RequestStatus.PENDING ||
				(!user && readUserRequest.status !== RequestStatus.ERROR) ||
				(user?.confirmed &&
					!skipProfileCheck &&
					userGrantsRequest.status !== RequestStatus.SUCCESS &&
					userGrantsRequest.status !== RequestStatus.ERROR))
		) {
			// Only show loading on initial mount — never blink back after content has rendered
			return <Loading />;
		}
		hasRenderedContent.current = true;

		return <C {...props}> </C>;
	};
	return ProtectedRoute;
};
